import { Text } from '@fluentui/react-components';
import { useCart } from 'contexts/CartContext/CartContext.hooks';
import StateDropdown from 'components/forms/inputs/StateDropdown';
import useBillingAddressFormStyles from './BillingAddressForm.styles';

import CheckboxField from 'components/forms/inputs/InputTypes/CheckboxField';
import BaseInputField from 'components/forms/inputs/BaseInputField/BaseInputField';
import { useEffect, useState } from 'react';
import HorizontalLine from '../../../../components/core/HorizontalLine/HorizontalLine';
import RemoveAutopayPopup from './RemoveAutopayPopup';
import DobInput from '../../../../components/forms/inputs/DobInput';

const BillingAddressForm = () => {
    const { cart, updateCustomerInfo, updateAutopay, updateEbill } = useCart();
    const styles = useBillingAddressFormStyles();

    const [firstName, setFirstName] = useState(
        cart.customerInfo.billingAddress?.firstName ?? cart.customerInfo.firstName
    );

    const [lastName, setLastName] = useState(
        cart.customerInfo.billingAddress?.lastName ?? cart.customerInfo.lastName
    );

    const [addressLine1, setAddressLine1] = useState(
        cart.customerInfo.billingAddress?.address.addressLine1 ??
            cart.customerInfo.address.addressLine1
    );
    const [addressLine2, setAddressLine2] = useState(
        cart.customerInfo.billingAddress?.address.addressLine2 ??
            cart.customerInfo.address.addressLine2
    );
    const [city, setCity] = useState(
        cart.customerInfo.billingAddress?.address.city ?? cart.customerInfo.address.city
    );
    const [state, setState] = useState(
        cart.customerInfo.billingAddress?.address.state ?? cart.customerInfo.address.state
    );
    const [zipcode, setZipcode] = useState(
        cart.customerInfo.billingAddress?.address.zipcode ?? cart.customerInfo.address.zipcode
    );

    const [dob, setDob] = useState(cart.customerInfo.billingAddress?.dob ?? cart.customerInfo.dob);

    const [isDifferentBillingAddress, setIsDifferentBillingAddress] = useState(
        cart.customerInfo.isDifferentBillingAddress
    );

    const [autopay, setAutopay] = useState(cart.autopay);
    const [showAutopayPopup, setShowAutopayPopup] = useState(false);

    const [eBill, setEBill] = useState(cart.eBill);

    useEffect(() => {
        updateCustomerInfo({
            ...cart.customerInfo,
            billingAddress: {
                firstName,
                lastName,
                address: {
                    addressLine1,
                    addressLine2,
                    city,
                    state,
                    zipcode
                },
                dob
            },
            isDifferentBillingAddress: isDifferentBillingAddress
        });
    }, [
        firstName,
        lastName,
        addressLine1,
        addressLine2,
        city,
        state,
        zipcode,
        dob,
        isDifferentBillingAddress
    ]);

    useEffect(() => {
        updateAutopay(autopay);
    }, [autopay]);

    useEffect(() => {
        updateEbill(eBill);
    }, [eBill]);

    const handleAutopayChange = (value: boolean) => {
        if (!value) setShowAutopayPopup(true);
        else setAutopay(true);
    };

    const handleAutopayPopupClose = (shouldUpdate: boolean) => {
        if (shouldUpdate) setAutopay(false);
        setShowAutopayPopup(false);
    };

    return (
        <>
            <div className={styles.autopayEbill}>
                <div className={styles.checkboxWrapper}>
                    <CheckboxField
                        checked={autopay}
                        onChange={(value) => handleAutopayChange(value)}
                    />
                    <Text size={500}>Enroll in autopay</Text>
                </div>

                <div className={styles.checkboxWrapper}>
                    <CheckboxField checked={eBill} onChange={(value) => setEBill(value)} />
                    <Text size={500}>Sign up for eBill</Text>
                </div>
            </div>

            <HorizontalLine className={styles.lineBreak} />

            <div className={styles.billingAddress}>
                <Text size={600}>Billing Address</Text>

                <div className={styles.checkboxWrapper}>
                    <CheckboxField
                        checked={!isDifferentBillingAddress}
                        onChange={(value) => setIsDifferentBillingAddress(!value)}
                    />
                    <Text size={500}>Same as customer address</Text>
                </div>
            </div>

            {isDifferentBillingAddress && (
                <form name={'customerInfoForm'} className={styles.inquiryContactInfo}>
                    <BaseInputField
                        name='firstName'
                        label='First Name'
                        className={styles.firstNameContainer}
                        required
                        defaultValue={firstName}
                        onChange={setFirstName}
                    />

                    <BaseInputField
                        name='lastName'
                        label='Last Name'
                        className={styles.lastNameContainer}
                        required
                        defaultValue={lastName}
                        onChange={setLastName}
                    />

                    <BaseInputField
                        name='addressLine1'
                        label='Address Line 1'
                        className={styles.address1Container}
                        required
                        defaultValue={addressLine1}
                        onChange={setAddressLine1}
                    />

                    <BaseInputField
                        name='addressLine2'
                        label='Address Line 2'
                        className={styles.address2Container}
                        defaultValue={addressLine2}
                        onChange={setAddressLine2}
                    />

                    <BaseInputField
                        name='city'
                        label='City'
                        className={styles.cityContainer}
                        required
                        defaultValue={city}
                        onChange={setCity}
                    />

                    <StateDropdown
                        className={styles.stateContainer}
                        required
                        value={state}
                        onChange={setState}
                    />

                    <BaseInputField
                        name='zipcode'
                        label='Zipcode'
                        className={styles.zipcodeContainer}
                        required
                        defaultValue={zipcode}
                        onChange={setZipcode}
                    />

                    <DobInput
                        value={dob}
                        className={styles.dobContainer}
                        required
                        onChange={setDob}
                    />
                </form>
            )}

            <RemoveAutopayPopup
                showPopup={showAutopayPopup}
                onDialogClose={handleAutopayPopupClose}
            />
        </>
    );
};

export default BillingAddressForm;
